/**
 * @file ThemeForm.js
 * @description Reusable form for displaying and editing an agency's Theme information.
 */

import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useMemo,
} from 'react';
import { isValidMobileIcon } from '../../utils/validate';
import style from './AgencyPage.module.scss';
import styles from '../theme/ThemePage.module.scss';
import Toast from '../ui/toasts/Toast';
import { updateAgency } from '../../services/systemService';
import { useAuthContext } from '../../store/AuthContext';
import Tooltip from '../ui/tooltips/Tooltip';
import FileUploader from '../theme/FileUploader';
import { SecurityAttributes } from '../../utils/data/enums';
import ColorBar from '../theme/ColorBar';
import LoginForm from '../auth/LoginForm';
import Header from '../header/Header';
import Navbar from '../navbar/Navbar';
import { getText } from '../../i18n';

const ThemeForm = forwardRef(({ onSave, initialData = {} }, ref) => {
    const isCreateMode = initialData?.id == -1;

    useImperativeHandle(ref, () => ({
        handleSave,
    }));

    const { hasAttribute } = useAuthContext();
    const canEdit = hasAttribute(SecurityAttributes.AgencyFullAccess);

    const [primaryLogo, setPrimaryLogo] = useState(null);
    const [secondaryLogo, setSecondaryLogo] = useState(null);
    const [mobileIcon, setMobileIcon] = useState(null);
    const [favicon, setFavicon] = useState(null);
    const [companyConfig, setCompanyConfig] = useState();

    const [applicationName, setApplicationName] = useState('');

    const [colors, setColors] = useState({
        header: { left: '#1366ff', right: '#17bdbf', type: 'gradient' },
        accessoriesBg: { left: '#1366ff', right: '#1366ff', type: 'single' },
        accessoriesFg: { left: '#ffffff', right: '#ffffff', type: 'single' },
    });

    // IMPORTANT: keep toggle as its own source of truth (prevents refresh/save race overwrites)
    const [enableClientTheme, setEnableClientTheme] = useState(true);

    const [loginBackgroundOption, setLoginBackgroundOption] = useState('color'); // "color" or "image"
    const [loginBackgroundColor, setLoginBackgroundColor] = useState({
        left: '#ffffff',
        right: '#ffffff',
        type: 'single',
    });
    const [loginBackgroundImage, setLoginBackgroundImage] = useState(null);

    const updateColors = section => newColors => {
        setColors(prev => ({ ...prev, [section]: newColors }));
    };

    const handleBackgroundOptionChange = event => {
        setLoginBackgroundOption(event.target.value);
    };

    function loadImageUrl(imageUrl) {
        try {
            return URL.createObjectURL(imageUrl);
        } catch (e) {
            return imageUrl;
        }
    }

    const getImageValue = (newFile, originalValue) => {
        if (newFile) {
            return newFile instanceof File ? loadImageUrl(newFile) : newFile;
        }
        return originalValue;
    };

    const updatePrimaryLogo = file => {
        setPrimaryLogo(file);
        setCompanyConfig(prev => ({
            ...prev,
            public_brand: {
                ...(prev?.public_brand || {}),
                logo_url: file ? loadImageUrl(file) : '',
            },
        }));
    };

    const setInitialThemeValues = () => {
        setPrimaryLogo(initialData.primaryLogo ?? null);
        setSecondaryLogo(initialData.secondaryLogo ?? null);
        setMobileIcon(initialData.mobileIcon ?? null);
        setFavicon(initialData.favicon ?? null);

        setApplicationName(initialData.applicationName || '');

        setColors({
            header: {
                left: initialData.pageHeaderColor1 || '#1366ff',
                right:
                    initialData.pageHeaderColor2 ||
                    initialData.pageHeaderColor1 ||
                    '#17bdbf',
                type: initialData.pageHeaderColor2 ? 'gradient' : 'single',
            },
            accessoriesBg: {
                left: initialData.accessoryColor || '#1366ff',
                right: initialData.accessoryColor || '#1366ff',
                type: 'single',
            },
            accessoriesFg: {
                left: initialData.accessoryTextColor || '#ffffff',
                right: initialData.accessoryTextColor || '#ffffff',
                type: 'single',
            },
        });

        setLoginBackgroundColor({
            left: initialData.loginBackgroundColor || '#f8fafc',
            right: initialData.loginBackgroundColor || '#f8fafc',
            type: 'single',
        });

        setLoginBackgroundImage(initialData.loginBackgroundImage ?? null);
        setLoginBackgroundOption(
            initialData?.loginBackgroundImage ? 'image' : 'color',
        );

        // Default ON only for create mode; otherwise respect BE value.
        setEnableClientTheme(
            isCreateMode ? true : (initialData?.enableClientTheme ?? true),
        );
    };

    useEffect(() => {
        setInitialThemeValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    const discardThemeChanges = () => {
        Toast.success(getText('msg_93fda44da931'));
        setInitialThemeValues();
    };

    const setPreviewStyles = () => {
        const { accessoriesBg, accessoriesFg } = colors;

        document.documentElement.style.setProperty(
            '--preview-border',
            accessoriesBg.type === 'single'
                ? `1px solid ${accessoriesBg.left}`
                : 'none',
        );

        document.documentElement.style.setProperty(
            '--preview-background',
            accessoriesBg.type === 'single'
                ? accessoriesBg.left
                : `linear-gradient(to right, ${accessoriesBg.left}, ${accessoriesBg.right})`,
        );

        document.documentElement.style.setProperty(
            '--preview-color',
            accessoriesFg.left || 'inherit',
        );
    };

    useEffect(() => {
        setPreviewStyles();
    }, [colors]);

    const loginFormBackgroundStyle =
        loginBackgroundOption === 'image' && loginBackgroundImage
            ? {
                  backgroundImage: `url(${loadImageUrl(loginBackgroundImage)})`,
                  backgroundSize: 'cover',
              }
            : {
                  background:
                      loginBackgroundOption === 'color'
                          ? loginBackgroundColor.type === 'gradient'
                              ? `linear-gradient(to right, ${loginBackgroundColor.left}, ${loginBackgroundColor.right})`
                              : loginBackgroundColor.left
                          : 'none',
              };

    // Build payload on demand (no derived-state loops)
    const themePayload = useMemo(() => {
        return {
            ...initialData,
            applicationName,
            primaryLogo: getImageValue(primaryLogo, initialData.primaryLogo),
            secondaryLogo: getImageValue(
                secondaryLogo,
                initialData.secondaryLogo,
            ),
            mobileIcon: getImageValue(mobileIcon, initialData.mobileIcon),
            favicon: getImageValue(favicon, initialData.favicon),
            pageHeaderColor1: colors.header.left,
            pageHeaderColor2:
                colors.header.type === 'gradient' ? colors.header.right : null,
            accessoryColor: colors.accessoriesBg.left,
            accessoryTextColor: colors.accessoriesFg.left,
            loginBackgroundColor: loginBackgroundColor.left,
            loginBackgroundImage:
                loginBackgroundOption === 'image'
                    ? getImageValue(
                          loginBackgroundImage,
                          initialData.loginBackgroundImage,
                      )
                    : null,
            enableClientTheme,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialData,
        applicationName,
        primaryLogo,
        secondaryLogo,
        mobileIcon,
        favicon,
        colors,
        loginBackgroundColor,
        loginBackgroundOption,
        loginBackgroundImage,
        enableClientTheme,
    ]);

    const resolveSaveOptions = argument => {
        if (argument && typeof argument.preventDefault === 'function') {
            return {};
        }

        return argument || {};
    };

    const handleSave = async invocation => {
        const options = resolveSaveOptions(invocation);
        const baseAgencyRecord = options.baseAgencyRecord || {};
        const payload = {
            ...baseAgencyRecord,
            ...themePayload,
            id: options.agencyId || themePayload.id,
        };

        return updateAgency(payload)
            .then(() => {
                Toast.success(getText('msg_d0fdcc99edf7'));

                if (!options.suppressOnSaveCallback) {
                    onSave?.();
                }

                return { success: true };
            })
            .catch(error => {
                const errors = error?.response?.data?.errors;
                if (errors) {
                    for (const key in errors) {
                        if (Object.prototype.hasOwnProperty.call(errors, key)) {
                            errors[key].forEach(msg => Toast.error(msg));
                        }
                    }
                } else {
                    Toast.error(getText('msg_6172a160eb34'));
                }
                console.error('Unable to update Theme', error);

                return { success: false, error };
            });
    };

    return (
        <form className={style.themeFormContainer}>
            <div className="col-lg-8 px-3 mt-5 m-auto">
                <section>
                    <div className="form-switch">
                        <input
                            onChange={() => setEnableClientTheme(prev => !prev)}
                            className="form-check-input custom-switch-input"
                            type="checkbox"
                            checked={!!enableClientTheme}
                            disabled={!canEdit}
                        />
                        <span
                            className="fw-medium"
                            style={{ marginLeft: '10px' }}
                        >
                            {getText('msg_6e15d96a6804')}
                        </span>
                    </div>
                </section>

                <section>
                    <div className="my-5">
                        <label htmlFor="applicationName" className="pb-3">
                            {getText('msg_4fb43108e78f')}
                        </label>
                        <input
                            id="applicationName"
                            type="text"
                            className="form-control"
                            value={applicationName}
                            onChange={e => {
                                const value = e.target.value;
                                if (value.length <= 50)
                                    setApplicationName(value);
                            }}
                            name="applicationName"
                            placeholder={getText('msg_e9c59a05e43f')}
                            disabled={!canEdit}
                        />
                        <small className="form-text text-muted">
                            {getText('msg_971d6ded4edf')} {applicationName.length}{getText('msg_2fd301945bd1')}
                        </small>
                    </div>
                </section>

                <section>
                    <div className="my-5">
                        <label htmlFor="primary-image-upload" className="pb-3">
                            {getText('msg_f76e9d2cf834')}
                            <sup>
                                <Tooltip
                                    html="Login Screen Logo will display on the login screen. If this is the only logo uploaded, it will also appear everywhere a logo is shown throughout the app."
                                    placement="right"
                                >
                                    <i
                                        className="bi bi-info-circle"
                                        style={{
                                            paddingLeft: '5px',
                                            fontSize: '13px',
                                            color: '#1366ff',
                                        }}
                                    />
                                </Tooltip>
                            </sup>
                        </label>
                        <FileUploader
                            id="primary-image-upload"
                            onFileChange={file => updatePrimaryLogo(file)}
                            initialFile={primaryLogo}
                            accept=".png"
                            inputText={getText('msg_feb8f43c6b5b')}
                            locked={!canEdit}
                        />
                    </div>

                    <div className="my-5">
                        <label
                            htmlFor="secondary-image-upload"
                            className="pb-3"
                        >
                            {getText('msg_afb30588070a')}
                            <sup>
                                <Tooltip html="Navigation Menu Logo will display above the navigation menu and everywhere a logo is shown throughout the site.">
                                    <i
                                        className="bi bi-info-circle"
                                        style={{
                                            paddingLeft: '5px',
                                            fontSize: '13px',
                                            color: '#1366ff',
                                        }}
                                    />
                                </Tooltip>
                            </sup>
                        </label>
                        <FileUploader
                            id="secondary-image-upload"
                            onFileChange={file => setSecondaryLogo(file)}
                            initialFile={secondaryLogo}
                            accept=".png"
                            inputText={getText('msg_feb8f43c6b5b')}
                            locked={!canEdit}
                        />
                    </div>

                    <div className="my-5">
                        <label htmlFor="mobile-icon-upload" className="pb-3">
                            {getText('msg_6b23c2d851de')}
                            <sup>
                                <Tooltip html="The mobile icon is used for mobile app installations and home screen shortcuts. Requirements: PNG format only, square dimensions (width = height), minimum 512x512 pixels.">
                                    <i
                                        className="bi bi-info-circle"
                                        style={{
                                            paddingLeft: '5px',
                                            fontSize: '13px',
                                            color: '#1366ff',
                                        }}
                                    />
                                </Tooltip>
                            </sup>
                        </label>
                        <FileUploader
                            id="mobile-icon-upload"
                            onFileChange={file => setMobileIcon(file)}
                            initialFile={mobileIcon}
                            accept=".png"
                            inputText={
                                <>
                                    {getText('msg_32e62dcbf83a')}
                                    <br />
                                    {getText('msg_f9bab8695ec5')}
                                </>
                            }
                            customValidation={isValidMobileIcon}
                            locked={!canEdit}
                        />
                    </div>

                    <div className="my-5">
                        <label htmlFor="favicon-upload" className="pb-3">
                            {getText('msg_b0f47aaf773c')}
                            <sup>
                                <Tooltip html="Favicon is the small icon displayed in browser tabs and bookmarks. It should be a .ico or .png file, ideally 16x16 or 32x32 pixels.">
                                    <i
                                        className="bi bi-info-circle"
                                        style={{
                                            paddingLeft: '5px',
                                            fontSize: '13px',
                                            color: '#1366ff',
                                        }}
                                    />
                                </Tooltip>
                            </sup>
                        </label>
                        <FileUploader
                            id="favicon-upload"
                            onFileChange={file => setFavicon(file)}
                            initialFile={favicon}
                            accept=".ico,.png"
                            inputText={getText('msg_1b9f1cdeaddf')}
                            locked={!canEdit}
                        />
                    </div>
                </section>

                <section className="py-5">
                    <label className="mb-3">{getText('msg_c9b9c46b1c2c')}</label>

                    <div className="d-flex inline-flex w-100 gap-3 pb-3">
                        <input
                            type="radio"
                            id="background-color"
                            name="backgroundOption"
                            value="color"
                            checked={loginBackgroundOption === 'color'}
                            onChange={handleBackgroundOptionChange}
                            disabled={!canEdit}
                        />
                        <ColorBar
                            initialColors={loginBackgroundColor}
                            onChangeComplete={setLoginBackgroundColor}
                            allowGradient={false}
                            readOnly={!canEdit}
                        />
                    </div>

                    <div className="d-flex inline-flex w-100 gap-3 pb-3">
                        <input
                            type="radio"
                            id="background-image"
                            name="backgroundOption"
                            value="image"
                            checked={loginBackgroundOption === 'image'}
                            onChange={handleBackgroundOptionChange}
                            className={styles.backgroundImage}
                            disabled={!canEdit}
                        />

                        <FileUploader
                            id="login-background-image-upload"
                            onFileChange={setLoginBackgroundImage}
                            initialFile={loginBackgroundImage}
                            accept=".png"
                            inputText={getText('msg_d9e61c2a5779')}
                            locked={!canEdit}
                        />
                    </div>
                </section>

                <div className="py-3">
                    <ColorBar
                        label={getText('msg_31341c6f0c7a')}
                        initialColors={colors.header}
                        onChangeComplete={updateColors('header')}
                        allowGradient={true}
                        toolTipText=""
                        readOnly={!canEdit}
                    />
                </div>

                <div className="py-3">
                    <ColorBar
                        label={getText('msg_700530084ce1')}
                        initialColors={colors.accessoriesBg}
                        onChangeComplete={updateColors('accessoriesBg')}
                        allowGradient={false}
                        toolTipText={getText('msg_0b3c57524d54')}
                        readOnly={!canEdit}
                    />
                </div>

                <div className="py-3">
                    <ColorBar
                        label={getText('msg_4647f7d57eb6')}
                        initialColors={colors.accessoriesFg}
                        onChangeComplete={updateColors('accessoriesFg')}
                        allowGradient={false}
                        toolTipText={getText('msg_1ae4ae4d3494')}
                        readOnly={!canEdit}
                    />
                </div>
            </div>

            <div className="d-flex mx-4 mt-5">
                <h5 className="pr-2 me-3">{getText('msg_f1fbb2b43dca')}</h5>
                <hr className={`w-100 ${styles.previewDivider}`} />
            </div>

            <div className={`col-lg-8 px-3 m-auto`}>
                <div
                    className={`autoMargin px-5 py-5  ${styles.logingPreview}`}
                    style={loginFormBackgroundStyle}
                >
                    <div
                        className={`ing-card modal-content ${styles.authContainer}`}
                    >
                        <LoginForm
                            onSuccess={() => {}}
                            handleAuthError={() => {}}
                            authData={{ companyConfig }}
                            demoOnly={true}
                            styleOverride={themePayload}
                        />
                    </div>
                </div>
            </div>

            <div className="d-block">
                <div className={styles.appPreviewContainer}>
                    <Header styleOverride={themePayload} />
                    <div className="middle">
                        <div className="d-flex">
                            <Navbar styleOverride={themePayload} />
                            <div className={`${styles.appPreview}`}>
                                <div className="page-content h-100">
                                    <div className="card h-100">
                                        <div className="card-body px-0 py-4 b">
                                            <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                                                <p className="page-title float-start card-title ms-4 mb-0">
                                                    {getText('msg_f1fbb2b43dca')}
                                                </p>

                                                <div className="float-end d-flex align-items-center">
                                                    <span className="ing-text-buttons mx-3">
                                                        <i className="bi bi-trash3 me-2" />
                                                        {getText('msg_f6fdbe48dc54')}
                                                    </span>
                                                    <span className="ing-text-buttons mx-3">
                                                        <i className="bi bi-copy me-2" />
                                                        {getText('msg_972d57379db3')}
                                                    </span>
                                                    <div to="new-portal-page">
                                                        <span
                                                            type="button"
                                                            className={`ms-3 me-4 button-base ing-button-primary-left ${styles.ingButtonPrimaryLeft}`}
                                                        >
                                                            <i className="bi bi-plus-lg me-1" />
                                                            {getText('msg_61cc55aa0453')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-center mb-5 me-4">
                <button
                    type="button"
                    onClick={() => discardThemeChanges()}
                    className="ing-popup-blue-negative submit-button-form mx-3"
                >
                    <i className="bi bi-x me-2" />
                    {getText('msg_77dfd2135f4d')}
                </button>

                {canEdit && (
                    <button
                        type="button"
                        onClick={handleSave}
                        className="mx-3 ing-button-primary-left align-self-baseline"
                    >
                        <i className="bi bi-check-lg me-2" />
                        {getText('msg_efc007a393f6')}
                    </button>
                )}
            </div>
        </form>
    );
});

export default ThemeForm;
